import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import i18n, { i18nReady } from "./i18n";
import { raceFirstPaint } from "./lib/paint-budget";
// The design-system layer (docs/spec/design-system.md §2): strict order —
// primitives, roles, element defaults, office components, floor scale. That is
// the WHOLE global stylesheet as of UX-4: the legacy `app.css` alias shim was
// dissolved into these five (see office.css's "legacy shared office grammar"
// header for the ledger of what went where and what was deleted).
import "./styles/tokens.css";
import "./styles/semantic.css";
import "./styles/base.css";
import "./styles/office.css";
import "./styles/floor.css";
// Script-specific font stacks and the self-hosted Devanagari font (D-430).
import "./styles/locales.css";
import { shouldRetryRead } from "./lib/query";
import { armRoleFeedRefresh } from "./lib/role-codes";
import { router } from "./router";

// Reads retry transient failures and NEVER retry a refusal — see
// `shouldRetryRead` for the measured cost of the react-query default (four 403s
// over ~8 s behind a loading skeleton, on a screen the role simply cannot read).
const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: shouldRetryRead } },
});
// A refusal from the server marks the role feed stale, so a role an admin
// trimmed stops being offered the trimmed area without a reload (D-586).
armRoleFeedRefresh(queryClient);

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("root element #root is missing from index.html");

// The first paint waits for the active language's dictionary, so a Russian
// floor phone never flashes English. A dictionary that fails to load still
// renders: i18next falls back to the bundled English. One that hangs renders
// in English after a short budget (lib/paint-budget.ts).
void raceFirstPaint(i18nReady, () => i18n.changeLanguage("en")).then(() => {
  createRoot(rootEl).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  );
});
